<script setup>
import { storeToRefs } from 'pinia'
import axios from '@axios'
import { useUsersStore } from '@/stores'

const usersStore = useUsersStore()
const { users } = storeToRefs(usersStore)

const member1 = ref(null)
const member2 = ref(null)
const loading = ref(false)
const result = ref(null)
const member1FriendIds = ref(new Set())
const loadingFriends = ref(false)

let searchTimer1 = null
let searchTimer2 = null

const onSearch1 = searchTerm => {
  if (!searchTerm || searchTerm.length < 2) return
  clearTimeout(searchTimer1)
  searchTimer1 = setTimeout(() => {
    usersStore.getSearchedUsers(searchTerm)
  }, 300)
}

const onSearch2 = searchTerm => {
  if (!searchTerm || searchTerm.length < 2) return
  clearTimeout(searchTimer2)
  searchTimer2 = setTimeout(() => {
    usersStore.getSearchedUsers(searchTerm)
  }, 300)
}

// When member 1 changes, fetch their friends so we can filter member 2 options
watch(member1, async newVal => {
  member1FriendIds.value = new Set()
  member2.value = null
  if (!newVal?._id) return
  loadingFriends.value = true
  try {
    const res = await axios.get(`/user/${newVal._id}`)
    const userData = res.data

    // A mutual friend is someone in both following and followersApproved
    const following = new Set((userData.following || []).map(id => id.toString()))
    const approved = new Set((userData.followersApproved || []).map(id => id.toString()))
    member1FriendIds.value = new Set([...following].filter(id => approved.has(id)))
  } catch {
    // If fetch fails, don't filter — allow all options
  } finally {
    loadingFriends.value = false
  }
})

const member2Items = computed(() => {
  let items = users.value

  // Exclude member 1 from member 2 options
  if (member1.value) {
    items = items.filter(u => u._id !== member1.value._id)
  }

  // Exclude member 1's existing friends
  if (member1FriendIds.value.size > 0) {
    items = items.filter(u => !member1FriendIds.value.has(u._id?.toString()))
  }
  
  return items
})

const userDisplayText = user => {
  if (!user) return ''
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
  
  return name ? `${name} (${user.email})` : (user.email || '')
}

const canConnect = computed(() => {
  return member1.value && member2.value && member1.value._id !== member2.value._id
})

const sameUserSelected = computed(() => {
  return member1.value && member2.value && member1.value._id === member2.value._id
})

const connect = async () => {
  if (!canConnect.value) return
  loading.value = true
  result.value = null
  try {
    const res = await axios.post('/user/admin-connect', {
      userId1: member1.value._id,
      userId2: member2.value._id,
    })
    result.value = res.data
    if (res.data.success) {
      member1.value = null
      member2.value = null
    }
  } catch (err) {
    result.value = { error: err.response?.data?.error || 'Something went wrong' }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="page-wrapper">
    <div class="page-header d-flex align-center mb-6">
      <RouterLink
        to="/admin/profile"
        class="back-btn"
      >
        <VIcon icon="mdi-arrow-left" />
      </RouterLink>
      <h1 class="page-title">
        Connect Members
      </h1>
    </div>

    <p class="text-medium-emphasis mb-6">
      Search for two members and connect them as friends. This creates a mutual connection instantly without sending notifications.
    </p>

    <VCard
      variant="outlined"
      class="pa-4 mb-4"
    >
      <div class="text-subtitle-2 text-medium-emphasis mb-2">
        Member 1
      </div>
      <VAutocomplete
        v-model="member1"
        :items="users"
        :item-title="userDisplayText"
        item-value="_id"
        return-object
        label="Search by name or email"
        clearable
        no-data-text="Type to search members"
        @update:search="onSearch1"
      />
    </VCard>

    <VCard
      variant="outlined"
      class="pa-4 mb-6"
    >
      <div class="text-subtitle-2 text-medium-emphasis mb-2">
        Member 2
      </div>
      <VAutocomplete
        v-model="member2"
        :items="member2Items"
        :item-title="userDisplayText"
        item-value="_id"
        return-object
        label="Search by name or email"
        clearable
        :disabled="!member1"
        :loading="loadingFriends"
        :no-data-text="loadingFriends ? 'Loading friends list...' : 'Type to search members'"
        @update:search="onSearch2"
      />
    </VCard>

    <div
      v-if="sameUserSelected"
      class="text-error text-body-2 mb-4"
    >
      Please select two different members.
    </div>

    <VBtn
      color="primary-dark"
      :disabled="!canConnect"
      :loading="loading"
      block
      size="large"
      @click="connect"
    >
      Connect Members
    </VBtn>

    <VAlert
      v-if="result?.success"
      type="success"
      variant="tonal"
      class="mt-4"
    >
      {{ result.message }}
    </VAlert>

    <VAlert
      v-if="result?.alreadyConnected"
      type="info"
      variant="tonal"
      class="mt-4"
    >
      {{ result.message }}
    </VAlert>

    <VAlert
      v-if="result?.error"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      {{ result.error }}
    </VAlert>
  </div>
</template>

<style lang="scss" scoped>
.page-wrapper {
  max-width: 600px;
  margin: 0 auto;
  padding: 16px;
}

.page-header {
  gap: 12px;
}

.back-btn {
  color: inherit;
  text-decoration: none;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: rgb(51, 51, 51);
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
